import React, { useImperativeHandle, forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { fetchSetup } from '@devexpress/analytics-core/analytics-utils';
import {
    DxReportViewer as WebDocumentViewer,
    RequestOptions,
    Callbacks
} from 'devexpress-reporting-react/dx-report-viewer';
import { useReporting } from '../providers/ReportingProvider.jsx';
import { DEFAULT_VIEWER_HEIGHT } from '../config/defaults.js';
import { registerDevExpressRequestHeaders } from '../config/devexpress-request-headers.js';
import { configureDevExpressLocalization } from '../localization/configure-devexpress-localization.js';
import { applyViewerExportFormatPolicy } from '../config/export-format-policy.js';
import { useReportingVendorChromeScope } from '../hooks/useReportingVendorChromeScope.js';
import {
    enterReadOnlyReportPreview,
    leaveReadOnlyReportPreview,
    enterEditableReportPreview,
    leaveEditableReportPreview,
} from '../utils/readOnlyReportPreviewStack.js';

/**
 * Enhanced Report Viewer Component
 *
 * Ref API:
 * - getViewerInstance() → DevExpress JSReportViewer (OpenReport, GetReportPreview, …)
 *
 * Props:
 * - reportUrl: The URL of the report to display
 * - height: Height of the component
 * - onParameterError: Callback for parameter validation errors
 * - onServerError: Callback for viewer initialization/export server failures
 * - onDocumentReady: Callback when report is fully loaded
 * - inheritProviderHeaders: false isolates session-token viewers from provider tenant/auth headers
 * - readOnly: true ise rapor düzenleme alanları kapalı (ticket “View report” gibi)
 */
export const ReportViewer = forwardRef(function ReportViewer(
    {
        reportUrl,
        height = DEFAULT_VIEWER_HEIGHT,
        callbacks: callbacksOverride,
        requestOptions: requestOptionsOverride,
        inheritProviderHeaders = true,
        onParameterError,
        onServerError,
        onDocumentReady,
        readOnly = false,
        exportFormats,
    },
    ref
) {
    const { viewerRequestOptions, locale, translate } = useReporting();
    useReportingVendorChromeScope();
    const viewerRef = useRef(null);
    const finalRequestOptions = requestOptionsOverride || !inheritProviderHeaders
        ? {
            ...viewerRequestOptions,
            ...(requestOptionsOverride || {}),
            headers: {
                ...(inheritProviderHeaders ? viewerRequestOptions.headers || {} : {}),
                ...(requestOptionsOverride?.headers || {}),
            },
        }
        : viewerRequestOptions;
    const {
        headers: requestHeaders,
        ...supportedRequestOptions
    } = finalRequestOptions;
    const requestHeadersSignature = JSON.stringify(requestHeaders || {});

    useLayoutEffect(
        () => registerDevExpressRequestHeaders(
            fetchSetup,
            JSON.parse(requestHeadersSignature),
            { exclusive: !inheritProviderHeaders },
        ),
        [requestHeadersSignature, inheritProviderHeaders],
    );
    /**
     * EditablePreviewEnabled global bir DevExpress ayarı olduğundan, viewer binding'den
     * önce mode'a göre (editable=true / readOnly=false) set edilmelidir; ilk karede içerik yok.
     */
    const [previewGateOpen, setPreviewGateOpen] = useState(false);

    useLayoutEffect(() => {
        if (readOnly) {
            enterReadOnlyReportPreview();
        } else {
            enterEditableReportPreview();
        }
        setPreviewGateOpen(true);
        return () => {
            if (readOnly) {
                leaveReadOnlyReportPreview();
            } else {
                leaveEditableReportPreview();
            }
            setPreviewGateOpen(false);
        };
    }, [readOnly]);

    useImperativeHandle(ref, () => ({
        getViewerInstance: () => viewerRef.current?.instance?.()
    }), []);

    const defaultCallbacks = {
        ParametersError: (args) => {
            if (onParameterError) onParameterError(args);
        },
        DocumentReady: (args) => {
            if (onDocumentReady) onDocumentReady(args);
        },
        OnServerError: (args) => {
            if (onServerError) onServerError(args);
            callbacksOverride?.OnServerError?.(args);
        },
        CustomizeMenuActions: () => {},
        CustomizeLocalization: (event) => {
            configureDevExpressLocalization(event, locale);
            callbacksOverride?.CustomizeLocalization?.(event);
        },
        CustomizeExportOptions: (event) => {
            // devexpress-reporting-react wraps the native event args.
            applyViewerExportFormatPolicy(event?.args ?? event, exportFormats);
            callbacksOverride?.CustomizeExportOptions?.(event);
        },
    };

    const finalCallbacks = {
        ...defaultCallbacks,
        ...callbacksOverride,
        CustomizeLocalization: defaultCallbacks.CustomizeLocalization,
        CustomizeExportOptions: defaultCallbacks.CustomizeExportOptions,
        OnServerError: defaultCallbacks.OnServerError,
    };

    return (
        <div className="reporting-viewer-container" style={{ width: '100%', height, position: 'relative' }}>
            {previewGateOpen ? (
                <WebDocumentViewer
                    key={locale}
                    ref={viewerRef}
                    reportUrl={reportUrl}
                    height={height}
                >
                    <RequestOptions {...supportedRequestOptions} />
                    <Callbacks {...finalCallbacks} />
                </WebDocumentViewer>
            ) : (
                <div
                    className="dx-designer flex h-full w-full items-center justify-center rounded-[var(--ds-radius-control)] border border-border bg-muted text-sm text-muted-foreground"
                    style={{ minHeight: '120px' }}
                >
                    {translate('loadingReport')}
                </div>
            )}
        </div>
    );
});

ReportViewer.displayName = 'ReportViewer';
